<template>
  <div class="supervisor-timesheet-tab">
    <supervisor-timesheet-toolbar
      :date-from.sync="dateFrom"
      :date-to.sync="dateTo"
      :selected-project-id.sync="selectedProjectId"
      :search-query.sync="searchQuery"
      :status-filter.sync="statusFilter"
      :week-label="weekLabel"
      :project-options="projectOptions"
      :status-filter-options="statusFilterOptions"
      :active-filter-count="activeFilterCount"
      :can-download-timesheet="canDownloadTimesheet"
      :export-disabled="filtered.length === 0"
      @refresh="fetchSheets"
      @export="openExportModal"
      @date-from-change="onDateFromChange"
      @shift-week="shiftWeek"
      @apply-current-week="resetToCurrentWeek"
    />

    <!-- Aggregate pending count, matching the "N pending" summary on the BA
         Timesheets tab's project-group header — same status-pill treatment,
         same underlying idea, applied here at the whole-list level since
         this tab has no per-project grouping. -->
    <div v-if="pendingCount > 0" class="supervisor-pending-summary">
      <span class="status-pill status-pending">
        {{ pendingCount }} pending review
      </span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="supervisor-loading">
      <b-spinner variant="primary" />
    </div>

    <!-- Empty state -->
    <div v-else-if="filtered.length === 0" class="supervisor-empty">
      <b-icon-clipboard class="supervisor-empty-icon" />
      <p v-if="sheets.length === 0">No supervisor timesheets yet.</p>
      <p v-else>No timesheets match your filters.</p>
    </div>

    <!-- Timesheet cards -->
    <div v-else class="supervisor-card-list">
      <div
        v-for="ts in filtered"
        :key="ts.id"
        class="timesheet-card"
        :class="{ 'timesheet-card-editing': editingId === ts.id }"
        :data-timesheet-id="ts.id"
      >
        <!-- Card header — always visible -->
        <supervisor-timesheet-card-header
          :ts="ts"
          :expanded="!!expanded[ts.id]"
          :editing="editingId === ts.id"
          @toggle="toggleExpand(ts.id)"
        />

        <!-- Expanded detail. `lazy` matters here: without it every card's
             body — including its full-resolution submission photos —
             mounts into the DOM (just display:none) for every sheet on the
             page, not only the expanded one, so the browser fetches every
             photo on every sheet up front. `lazy` defers that render until
             a card is first opened, and (per BootstrapVue) leaves it mounted
             after, which the edit trigger below relies on since it only
             becomes reachable once a card is already expanded. -->
        <b-collapse :visible="!!expanded[ts.id]" lazy>
          <div
            class="card-body-soft"
            :class="{ 'card-body-editing': editingId === ts.id }"
          >
            <div class="detail-section">
              <div class="detail-section-heading">
                <div>
                  <h3 class="detail-section-title">Submission details</h3>
                  <p class="detail-section-description">
                    Notes and supporting photos provided by the supervisor.
                  </p>
                </div>
              </div>
              <supervisor-submission-context
                v-if="editingId !== ts.id"
                :comment="ts.supervisor_comment"
                :photos="ts.photo_urls || []"
                @open-lightbox="openLightbox(ts.photo_urls || [], $event)"
              />
              <template v-else>
                <b-form-textarea
                  v-model="editComment"
                  placeholder="Add a comment..."
                  rows="2"
                  max-rows="6"
                  maxlength="1000"
                />
                <supervisor-submission-context
                  v-if="(ts.photo_urls || []).length"
                  :photos="ts.photo_urls || []"
                  @open-lightbox="openLightbox(ts.photo_urls || [], $event)"
                />
              </template>
            </div>

            <!-- Rejection info -->
            <div v-if="ts.status === 'rejected'" class="rejection-panel">
              <span class="rejection-panel-icon" aria-hidden="true">
                <b-icon-exclamation-circle />
              </span>
              <div class="rejection-panel-content">
                <strong>Rejection reason</strong>
                <span>{{ ts.rejection_reason }}</span>
                <p v-if="ts.rejection_comment">
                  {{ ts.rejection_comment }}
                </p>
              </div>
            </div>

            <div class="detail-section-heading worker-section-heading">
              <div>
                <h3 class="detail-section-title">
                  {{
                    editingId === ts.id ? 'Edit worker hours' : 'Worker hours'
                  }}
                </h3>
                <p class="detail-section-description">
                  {{
                    editingId === ts.id
                      ? 'Update attendance, leave, start and finish times for this week.'
                      : 'Review the submitted hours and on-site verification for each worker.'
                  }}
                </p>
              </div>
              <span class="worker-count-pill">
                <b-icon-people />
                {{ tokenRows(ts).length }}
                worker{{ tokenRows(ts).length !== 1 ? 's' : '' }}
              </span>
            </div>

            <supervisor-worker-table
              :sheet="ts"
              :editing="editingId === ts.id"
              :edit-rows="editRows"
              :view-rows="pagedTokenRows(ts)"
              :week-day-headers="weekDays(ts.week_start)"
              :leave-type-options="leaveTypeOptions"
              :can-manage-plant="canManagePlant"
              :editing-plant-entry-id="editingPlantEntryId"
              :plant-entry-edit-quantity="plantEntryEditQuantity"
              :plant-entry-saving="plantEntrySaving"
              :row-zone-status="rowZoneStatus"
              :zone-status-variant="zoneStatusVariant"
              :zone-status-label="zoneStatusLabel"
              @toggle-worker="toggleWorker"
              @set-present="setPresent"
              @set-day-field="setDayField"
              @set-leave-type="setLeaveType"
              @set-leave-hours="setLeaveHours"
              @update:plantEntryEditQuantity="plantEntryEditQuantity = $event"
              @start-plant-entry-edit="startPlantEntryEdit"
              @save-plant-entry-edit="savePlantEntryEdit"
              @cancel-plant-entry-edit="cancelPlantEntryEdit"
              @open-amendment-history="openAmendmentHistory"
            />

            <supervisor-worker-cards
              :sheet="ts"
              :editing="editingId === ts.id"
              :edit-rows="editRows"
              :view-rows="pagedTokenRows(ts)"
              :week-day-headers="weekDays(ts.week_start)"
              :leave-type-options="leaveTypeOptions"
              :can-manage-plant="canManagePlant"
              :editing-plant-entry-id="editingPlantEntryId"
              :plant-entry-edit-quantity="plantEntryEditQuantity"
              :plant-entry-saving="plantEntrySaving"
              :row-zone-status="rowZoneStatus"
              :zone-status-variant="zoneStatusVariant"
              :zone-status-label="zoneStatusLabel"
              @toggle-worker="toggleWorker"
              @set-present="setPresent"
              @set-day-field="setDayField"
              @set-leave-type="setLeaveType"
              @set-leave-hours="setLeaveHours"
              @update:plantEntryEditQuantity="plantEntryEditQuantity = $event"
              @start-plant-entry-edit="startPlantEntryEdit"
              @save-plant-entry-edit="savePlantEntryEdit"
              @cancel-plant-entry-edit="cancelPlantEntryEdit"
              @open-amendment-history="openAmendmentHistory"
            />

            <!-- Worker list pagination (view mode only) -->
            <div
              v-if="
                editingId !== ts.id && tokenRows(ts).length > workerPageSize
              "
              class="d-flex align-items-center justify-content-between mb-3"
            >
              <small class="text-muted">
                {{ (workerPageFor(ts) - 1) * workerPageSize + 1 }}–{{
                  Math.min(
                    workerPageFor(ts) * workerPageSize,
                    tokenRows(ts).length,
                  )
                }}
                of {{ tokenRows(ts).length }} workers
              </small>
              <b-pagination
                :value="workerPageFor(ts)"
                :total-rows="tokenRows(ts).length"
                :per-page="workerPageSize"
                size="sm"
                class="mb-0"
                hide-goto-end-buttons
                @input="setWorkerPage(ts, $event)"
              />
            </div>

            <!--
              Crew plant & equipment (sheet-level). Renders in both view and
              edit mode. View mode still gates on the array being non-empty
              (nothing captured, nothing to show); edit mode also shows an
              empty section so there's somewhere to click "Add equipment" —
              but only when the company hasn't turned crew-equipment logging
              off, so supervisors who don't use it never see a dead control.
            -->
            <template
              v-if="
                (ts.plant_entries && ts.plant_entries.length) ||
                (editingId === ts.id && canAddPlant)
              "
            >
              <div class="detail-section-heading worker-section-heading">
                <div>
                  <h3 class="detail-section-title">Plant &amp; equipment</h3>
                  <p class="detail-section-description">
                    Crew plant and equipment recorded against this timesheet.
                  </p>
                </div>
                <div class="crew-plant-heading-actions">
                  <span
                    v-if="
                      showPlantCost &&
                      ts.plant_entries &&
                      ts.plant_entries.length
                    "
                    class="crew-plant-total-pill"
                  >
                    Plant total ${{ formatCurrency(crewPlantTotal(ts)) }}
                  </span>
                  <button
                    v-if="editingId === ts.id && canAddPlant"
                    type="button"
                    class="crew-plant-add-btn"
                    @click="openAddPlantModal(ts)"
                  >
                    + Add equipment
                  </button>
                </div>
              </div>

              <supervisor-crew-plant-panel
                :entries="
                  editingId === ts.id
                    ? editPlantEntries || []
                    : ts.plant_entries
                "
                :editable="editingId === ts.id"
                :show-cost="showPlantCost"
                @remove="removeEditPlantEntry"
              />
              <supervisor-crew-plant-cards
                :entries="
                  editingId === ts.id
                    ? editPlantEntries || []
                    : ts.plant_entries
                "
                :editable="editingId === ts.id"
                :show-cost="showPlantCost"
                @remove="removeEditPlantEntry"
              />
            </template>

            <!-- Invite a new worker to the company (edit mode only) -->
            <supervisor-invite-tradie-panel
              v-if="editingId === ts.id"
              :form="newPersonForm"
              :loading="actionLoading === ts.id"
              @invite="addPersonToSheet(ts)"
            />

            <!-- Invited tradies without a token yet — no hours possible until eligible -->
            <supervisor-pending-workers-panel
              :workers="ts.pending_workers || []"
            />

            <!-- Audit trail -->
            <supervisor-audit-trail
              v-if="ts.audit && ts.audit.length"
              :audit="ts.audit"
            />

            <supervisor-sheet-actions
              :status="ts.status"
              :editing="editingId === ts.id"
              :loading="actionLoading === ts.id"
              :edit-rows-have-errors="
                editingId === ts.id && editRowsHaveErrors()
              "
              :can-edit-timesheets="canEditTimesheets"
              :can-delete-timesheets="canDeleteTimesheets"
              :can-edit-drafts="canEditDrafts"
              @approve="approveSheet(ts)"
              @reject="openRejectModal(ts)"
              @edit="startEdit(ts)"
              @submit="submitDraft(ts)"
              @save="saveEdit(ts)"
              @cancel="cancelEdit"
              @recall="recallSheet(ts)"
              @delete="deleteDraft(ts)"
            />
          </div>
        </b-collapse>
      </div>
    </div>

    <supervisor-reject-modal
      :reject-form.sync="rejectForm"
      :rejection-reasons="rejectionReasons"
      @submit="submitReject"
      @hidden="resetRejectForm"
    />

    <supervisor-export-modal
      :export-tradie-search.sync="exportTradieSearch"
      :worker-filter.sync="workerFilter"
      :export-format.sync="exportFormat"
      :filtered-worker-options="filteredWorkerOptions"
      :all-filtered-tradies-selected="allFilteredTradiesSelected"
      @toggle-all-tradies="toggleAllTradies"
      @export="exportReport"
    />

    <supervisor-photo-lightbox
      :photos="lightboxPhotos"
      :index.sync="lightboxIndex"
      @hidden="resetLightbox"
    />

    <!-- Plant entry amendment history -->
    <plant-entry-amendment-history-modal
      v-model="amendmentHistoryOpen"
      :entry-id="amendmentHistoryEntryId"
      :plant-name="amendmentHistoryPlantName"
    />

    <!-- Add crew equipment (sheet-level, edit mode only) -->
    <add-crew-plant-modal
      modal-id="add-crew-plant-modal-admin"
      :week-dates="addPlantModalWeekDates"
      :equipment-options="crewEquipmentOptions"
      @save="handleAddPlantSave"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  formatTrade,
  dayMinutes,
  formatCurrency,
  formatHours,
  isOvernight,
  dayError as sharedDayError,
  LEAVE_TYPE_OPTIONS,
  leaveTypeLabel,
  isLeaveDay,
  dayLeaveHours,
  leaveHoursError,
  withToggledWorker,
  withSetPresent,
  withSetDayField,
  withSetLeaveType,
  withSetLeaveHours,
  buildEditRowsPayload,
  buildPlantEntriesPayload,
  buildZoneVerifiedDayKeys,
  rowZoneStatus as computeRowZoneStatus,
  dayZoneStatus as computeDayZoneStatus,
  zoneStatusVariant as computeZoneStatusVariant,
  zoneStatusLabel as computeZoneStatusLabel,
  dayHoursDecimal,
  leaveHoursExport as leaveHoursExportHelper,
  buildSupervisorExportRows,
  tokenRows,
  supervisorStatusVariant as statusVariant,
  formatWeekRange as formatWeek,
  buildEquipmentOptions,
} from '@/utils/timesheetHelpers'
import {
  startDayOf,
  weekDates,
  addDays,
  daySpan,
  formatDayHeader,
} from '@/utils/weekHelpers'
import { downloadRowsAsPdf, downloadRowsAsCsv } from '@/utils/pdfExport'
import PlantEntryAmendmentHistoryModal from '@/components/timesheets/PlantEntryAmendmentHistoryModal.vue'
import AddCrewPlantModal from '@/components/timesheets/supervisor/AddCrewPlantModal.vue'
import SupervisorWorkerTable from '@/components/timesheets/supervisor/SupervisorWorkerTable.vue'
import SupervisorWorkerCards from '@/components/timesheets/supervisor/SupervisorWorkerCards.vue'
import SupervisorCrewPlantPanel from '@/components/timesheets/supervisor/SupervisorCrewPlantPanel.vue'
import SupervisorCrewPlantCards from '@/components/timesheets/supervisor/SupervisorCrewPlantCards.vue'
import SupervisorTimesheetToolbar from '@/components/timesheets/supervisor/SupervisorTimesheetToolbar.vue'
import SupervisorRejectModal from '@/components/timesheets/supervisor/SupervisorRejectModal.vue'
import SupervisorExportModal from '@/components/timesheets/supervisor/SupervisorExportModal.vue'
import SupervisorPhotoLightbox from '@/components/timesheets/supervisor/SupervisorPhotoLightbox.vue'
import SupervisorTimesheetCardHeader from '@/components/timesheets/supervisor/SupervisorTimesheetCardHeader.vue'
import SupervisorSubmissionContext from '@/components/timesheets/supervisor/SupervisorSubmissionContext.vue'
import SupervisorInviteTradiePanel from '@/components/timesheets/supervisor/SupervisorInviteTradiePanel.vue'
import SupervisorAuditTrail from '@/components/timesheets/supervisor/SupervisorAuditTrail.vue'
import SupervisorPendingWorkersPanel from '@/components/timesheets/supervisor/SupervisorPendingWorkersPanel.vue'
import SupervisorSheetActions from '@/components/timesheets/supervisor/SupervisorSheetActions.vue'
import Api from '@/api'

const REJECTION_REASONS = [
  { value: null, text: '- Select a reason -', disabled: true },
  { value: 'Incorrect hours', text: 'Incorrect hours' },
  { value: 'Missing workers', text: 'Missing workers' },
  { value: 'Duplicate submission', text: 'Duplicate submission' },
  { value: 'Incorrect week', text: 'Incorrect week' },
  {
    value: 'Hours not matching site records',
    text: 'Hours not matching site records',
  },
  { value: 'Other', text: 'Other' },
]

export default {
  name: 'SupervisorTimesheetTab',
  components: {
    PlantEntryAmendmentHistoryModal,
    AddCrewPlantModal,
    SupervisorWorkerTable,
    SupervisorWorkerCards,
    SupervisorCrewPlantPanel,
    SupervisorCrewPlantCards,
    SupervisorTimesheetToolbar,
    SupervisorRejectModal,
    SupervisorExportModal,
    SupervisorPhotoLightbox,
    SupervisorTimesheetCardHeader,
    SupervisorSubmissionContext,
    SupervisorInviteTradiePanel,
    SupervisorAuditTrail,
    SupervisorPendingWorkersPanel,
    SupervisorSheetActions,
  },

  props: {
    active: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      sheets: [],
      zoneEvents: [],
      statusFilter: ['draft', 'submitted', 'approved', 'rejected'],
      expanded: {},
      workerPageSize: 10,
      workerPageByTs: {},
      actionLoading: null,
      rejectForm: { sheetId: null, reason: null, comment: '' },
      rejectionReasons: REJECTION_REASONS,
      editingId: null,
      editRows: null,
      editComment: '',
      editUpdatedAt: null,
      // Detached working copy of the sheet's crew plant entries, like
      // editRows — additions/removals stage here and only reach the API (and
      // the live ts.plant_entries the rest of the screen reads) on Save, so
      // Cancel genuinely discards them.
      editPlantEntries: null,
      plantEntriesDirty: false,
      addPlantModalWeekDates: [],
      crewEquipmentOptions: [],
      newPersonForm: { name: '', email: '' },
      dateFrom: '',
      dateTo: '',
      selectedProjectId: null,
      searchQuery: '',
      exportTradieSearch: '',
      workerFilter: [],
      exportFormat: 'csv',
      lightboxPhotos: [],
      lightboxIndex: 0,
      editingPlantEntryId: null,
      plantEntryEditQuantity: null,
      plantEntrySaving: false,
      amendmentHistoryOpen: false,
      amendmentHistoryEntryId: null,
      amendmentHistoryPlantName: '',
      statusFilterOptions: [
        { value: 'draft', text: 'Draft' },
        { value: 'submitted', text: 'Pending' },
        { value: 'approved', text: 'Approved' },
        { value: 'rejected', text: 'Rejected' },
      ],
    }
  },

  computed: {
    ...mapGetters('auth', ['user', 'hasPermission']),
    ...mapGetters('timesheet', ['timesheetSettings']),

    weekLabel() {
      if (!this.dateFrom || !this.dateTo) return '-'
      const from = new Date(this.dateFrom + 'T00:00:00')
      const to = new Date(this.dateTo + 'T00:00:00')
      const month = (d) => d.toLocaleDateString('en-AU', { month: 'short' })
      // Non-breaking space inside each date so the label can only ever wrap
      // around the dash, never mid-date.
      const nbsp = '\u00A0'
      if (
        from.getMonth() === to.getMonth() &&
        from.getFullYear() === to.getFullYear()
      ) {
        // Same month: compact "13–19 Jul" fits phone-width filter columns.
        return `${from.getDate()}–${to.getDate()}${nbsp}${month(to)}`
      }
      return `${from.getDate()}${nbsp}${month(
        from,
      )} – ${to.getDate()}${nbsp}${month(to)}`
    },

    canDownloadTimesheet() {
      return this.hasPermission('timesheets.download')
    },

    canDeleteTimesheets() {
      return this.hasPermission('timesheets.delete')
    },

    canEditTimesheets() {
      return this.hasPermission('timesheets.edit')
    },

    // Narrower than timesheets.edit — see PermissionCatalogue in the API repo.
    // Editing a submitted/rejected sheet is a correction to work already
    // handed in; taking over someone's unfinished draft is a bigger capability,
    // so it's gated separately rather than implied by timesheets.edit.
    canEditDrafts() {
      return this.hasPermission('timesheets.edit_drafts')
    },

    canManagePlant() {
      return this.hasPermission('timesheets.plant.manage')
    },

    // This tab is business-admin-only today, but supervisors must never see
    // dollar rates/claims/totals — same rule SupervisorSubmitHistory.vue and
    // SupervisorSubmitForm.vue follow — so guard by role here too rather than
    // relying solely on the route gate in TimesheetsPage.vue.
    showPlantCost() {
      return !(
        this.user &&
        this.user.user_type === 'tradie' &&
        this.user.is_supervisor
      )
    },

    // Company-wide opt-out, mirrors the API's own gate in _saveSheetPlant —
    // keep the control hidden here too so a disabled company never sees a
    // button that would silently no-op on save.
    crewEquipmentEnabled() {
      const settings = this.$store.getters['timesheet/timesheetSettings']
      return settings
        ? settings.supervisor_crew_equipment_enabled !== false
        : true
    },

    canAddPlant() {
      return this.canManagePlant && this.crewEquipmentEnabled
    },

    filtered() {
      const q = this.searchQuery.trim().toLowerCase()
      const from = this.dateFrom ? new Date(this.dateFrom + 'T00:00:00') : null
      const to = this.dateTo ? new Date(this.dateTo + 'T23:59:59') : null

      return this.sheets.filter((ts) => {
        if (
          this.selectedProjectId != null &&
          ts.project_id !== this.selectedProjectId
        )
          return false
        if (this.statusFilter.length && !this.statusFilter.includes(ts.status))
          return false
        if (from || to) {
          const weekStart = new Date(ts.week_start + 'T00:00:00')
          const weekEnd = new Date(weekStart)
          weekEnd.setDate(weekEnd.getDate() + 6)
          if (from && weekEnd < from) return false
          if (to && weekStart > to) return false
        }
        if (q) {
          const matchesProject = ts.project_name.toLowerCase().includes(q)
          const matchesWorker = (ts.rows || []).some((row) =>
            row.worker.full_name.toLowerCase().includes(q),
          )
          if (!matchesProject && !matchesWorker) return false
        }
        return true
      })
    },

    activeFilterCount() {
      const n = this.statusFilter.length
      return n === 0 || n === this.statusFilterOptions.length ? 0 : n
    },

    pendingCount() {
      return this.filtered.filter((ts) => ts.status === 'submitted').length
    },

    // Shared with the other timesheet tabs — a stable, company-wide project
    // list (fetched once, see mounted()) rather than derived from `sheets`.
    // `sheets` is now scoped to the visible date/project range (see
    // fetchSheets()), so deriving options from it would drop a project from
    // this dropdown the moment it has no sheet in the current week, making
    // it unselectable to go widen the range and find its older history.
    projectOptions() {
      return this.$store.getters['timesheet/projectOptions']
    },

    workerOptions() {
      const seen = {}
      this.sheets.forEach((ts) => {
        ;(ts.rows || []).forEach((row) => {
          if (row.worker && row.worker.id != null) {
            seen[row.worker.id] = row.worker.full_name
          }
        })
      })
      return Object.keys(seen)
        .map((id) => ({ value: Number(id), text: seen[id] }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    filteredWorkerOptions() {
      const q = this.exportTradieSearch.trim().toLowerCase()
      if (!q) return this.workerOptions
      return this.workerOptions.filter((opt) =>
        opt.text.toLowerCase().includes(q),
      )
    },

    allFilteredTradiesSelected() {
      return (
        this.filteredWorkerOptions.length > 0 &&
        this.filteredWorkerOptions.every((opt) =>
          this.workerFilter.includes(opt.value),
        )
      )
    },

    // Set of `${workerId}|YYYY-MM-DD` keys for clock-ins that were
    // geofence-confirmed, used to cross-check the timesheet's declared
    // on-site days against real zone check-in data.
    zoneVerifiedDayKeys() {
      return buildZoneVerifiedDayKeys(this.zoneEvents)
    },

    leaveTypeOptions() {
      return LEAVE_TYPE_OPTIONS
    },
  },

  watch: {
    active(val) {
      if (val) this.fetchSheets()
    },
    // Selecting a project re-anchors the visible week to that project's
    // pay-period start day (its override, or the company default), which
    // changes dateFrom/dateTo. fetchSheets() is scoped to that range server
    // -side (see fetchSheets()), so it must be re-run here — a stray
    // fetchZoneEvents()-only call left sheets stale/empty for the new
    // window. fetchSheets() re-runs fetchZoneEvents() itself.
    selectedProjectId() {
      this.applyCurrentWeek()
      this.fetchSheets()
    },
    // The pay period can be changed on the Dashboard settings card while this
    // tab stays mounted. Re-anchor when the schedule actually changes (skip
    // the first null → object load and leave-defaults-only saves).
    timesheetSettings(newVal, oldVal) {
      if (!oldVal || !newVal) return
      const changed =
        oldVal.pay_period_weeks !== newVal.pay_period_weeks ||
        oldVal.pay_period_anchor_date !== newVal.pay_period_anchor_date ||
        oldVal.start_day !== newVal.start_day
      if (!changed) return
      this.applyCurrentWeek()
      this.fetchSheets()
    },
  },

  async mounted() {
    if (!this.$store.state.project.projects.length) {
      this.$store
        .dispatch('project/getProjects', { limit: 100, is_archived: false })
        .catch(function () {})
    }
    // Cached after first load — cheap. Must resolve before applyCurrentWeek()
    // so the default week honours the business's configured start day.
    await this.$store.dispatch('timesheet/getTimesheetSettings')
    // Per-project pay-period overrides so applyCurrentWeek() can honour a
    // project on a different week. Cached; failure falls back to company.
    // Must also resolve before applyCurrentWeek() — otherwise the first
    // computed range silently uses the company default even for a project
    // with its own override, since the override list hasn't loaded yet.
    await this.$store.dispatch('timesheet/getProjectPayPeriods')
    this.applyCurrentWeek()
    if (this.active) this.fetchSheets()
  },

  methods: {
    formatTrade,
    tokenRows,
    statusVariant,
    formatWeek,

    async fetchSheets() {
      this.loading = true
      try {
        // Scoped to the currently visible date/project range — the API used
        // to return the company's entire unbounded timesheet history (every
        // sheet, every week, forever) on every load, which is what made this
        // tab slow to the point of a multi-second, multi-MB response as
        // history grew. _autoExpandFromQuery() below backfills a single sheet
        // via a targeted fetch if a notification link points outside this
        // range, so deep links still work without re-widening this query.
        const params = {}
        if (this.dateFrom) params.week_start_from = this.dateFrom
        if (this.dateTo) params.week_start_to = this.dateTo
        if (this.selectedProjectId != null)
          params.project_id = this.selectedProjectId
        // fetchZoneEvents() derives its window from dateFrom/dateTo directly
        // (already known here), not from the sheets this fetches, so it no
        // longer needs to wait for this response — run them together instead
        // of paying two full round trips back to back. Trade-off:
        // _autoExpandFromQuery() below can still widen dateFrom/dateTo for a
        // notification deep link to a sheet outside the current range; that
        // one sheet's zone events won't be covered until the next natural
        // refresh, which is fine since fetchZoneEvents() already treats a
        // gap as non-fatal (falls back to "Unverified").
        const [sheets] = await Promise.all([
          this.$store.dispatch('timesheet/getSupervisorTimesheets', params),
          this.fetchZoneEvents(),
        ])
        this.sheets = sheets
        await this._autoExpandFromQuery()
      } catch (e) {
        this.$bvToast.toast('Failed to load supervisor timesheets.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.loading = false
      }
    },

    // Loads real geofence check-in events covering the currently visible
    // date/project range, so the "On Site" column can cross-check the
    // declared on-site days against actual attendance instead of just the
    // supervisor's manual toggle — without pulling the company's entire
    // timesheet history on every load.
    async fetchZoneEvents() {
      if (!this.dateFrom || !this.dateTo) {
        this.zoneEvents = []
        return
      }
      // dateTo is already the inclusive last day of the visible range —
      // applyCurrentWeek()/onDateFromChange()/shiftWeek() all set it that
      // way (weekDates(...)[6] or a pay-period end_date), same value this
      // used to re-derive from the loaded sheets' week_start range.
      const dateFrom = this.dateFrom
      const dateToStr = this.dateTo

      try {
        const baseParams = {
          date_from: dateFrom,
          date_to: dateToStr,
          per_page: 100,
        }
        if (this.selectedProjectId != null)
          baseParams.project_id = this.selectedProjectId

        // Page 1 first — it's the only way to learn last_page. Any
        // remaining pages don't depend on each other, so fetch them
        // together instead of one page at a time.
        const first = await Api.get('/timesheet-events', {
          params: { ...baseParams, page: 1 },
        })
        let allEvents = first.data.events || []
        const lastPage =
          (first.data.meta && first.data.meta.last_page) ||
          first.data.last_page ||
          1
        if (lastPage > 1) {
          const rest = await Promise.all(
            Array.from({ length: lastPage - 1 }, (_, i) =>
              Api.get('/timesheet-events', {
                params: { ...baseParams, page: i + 2 },
              }),
            ),
          )
          rest.forEach((res) => {
            allEvents = allEvents.concat(res.data.events || [])
          })
        }
        this.zoneEvents = allEvents
      } catch (e) {
        // Non-fatal: the table still works with the manual on_site toggle,
        // it just falls back to showing everything as unverified.
        this.zoneEvents = []
      }
    },

    workerPageFor(ts) {
      return this.workerPageByTs[ts.id] || 1
    },

    setWorkerPage(ts, page) {
      this.$set(this.workerPageByTs, ts.id, page)
    },

    pagedTokenRows(ts) {
      const rows = this.tokenRows(ts)
      const page = this.workerPageFor(ts)
      const start = (page - 1) * this.workerPageSize
      return rows.slice(start, start + this.workerPageSize)
    },

    // 'verified' | 'unverified' | 'not-worked' for the whole row/week —
    // verified only if every day the worker was marked present also has a
    // matching geofence-confirmed clock-in.
    rowZoneStatus(row) {
      return computeRowZoneStatus(row, this.zoneVerifiedDayKeys)
    },

    // Per-day equivalent of rowZoneStatus(), used by the day-per-row export
    // — a leave day was never on site, so it always reads 'not-worked'.
    dayZoneStatus(row, day) {
      return computeDayZoneStatus(row, day, this.zoneVerifiedDayKeys)
    },

    // Decimal leave hours for the export's Hours column, blank when unset.
    leaveHoursExport(day) {
      return leaveHoursExportHelper(day)
    },

    zoneStatusVariant(status) {
      return computeZoneStatusVariant(status)
    },

    zoneStatusLabel(status) {
      return computeZoneStatusLabel(status)
    },

    async _autoExpandFromQuery() {
      const id = parseInt(this.$route.query.timesheetId, 10)
      if (!id) return
      let sheet = this.sheets.find((s) => s.id === id)
      // fetchSheets() now scopes its request to the visible date/project
      // range (see the comment there), so a notification linking to a sheet
      // from outside that range won't be in this.sheets. Fetch it directly
      // rather than re-widening the list query back to unbounded.
      if (!sheet) {
        try {
          sheet = await this.$store.dispatch(
            'timesheet/getSupervisorTimesheetSingle',
            id,
          )
        } catch (e) {
          return
        }
        if (!sheet) return
        this.sheets = [...this.sheets, sheet]
      }
      // Ensure the sheet isn't hidden by the status filter
      if (!this.statusFilter.includes(sheet.status)) {
        this.statusFilter = [...this.statusFilter, sheet.status]
      }
      // Ensure the sheet's week isn't hidden by the default current-week
      // date range — notifications can point at a timesheet from a
      // different week than the one currently displayed.
      if (sheet.week_start) {
        const weekEnd = weekDates(sheet.week_start)[6]
        if (!this.dateFrom || sheet.week_start < this.dateFrom) {
          this.dateFrom = sheet.week_start
        }
        if (!this.dateTo || weekEnd > this.dateTo) {
          this.dateTo = weekEnd
        }
      }
      this.$set(this.expanded, id, true)
      this.$nextTick(() => {
        const el = document.querySelector(`[data-timesheet-id="${id}"]`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },

    applyCurrentWeek() {
      // Use the resolved current pay period for the selected scope (its
      // override, or the company default) — the full span, so a fortnightly
      // schedule fills 14 days rather than 7.
      const schedule = this.$store.getters['timesheet/projectPayPeriod'](
        this.selectedProjectId,
      )
      const cp = schedule && schedule.current_period
      if (cp && cp.start_date && cp.end_date) {
        this.dateFrom = cp.start_date
        this.dateTo = cp.end_date
        return
      }
      // Fallback (settings not loaded yet): a 7-day week off the start day.
      const startDay = this.$store.getters['timesheet/projectStartDay'](
        this.selectedProjectId,
      )
      const weekStart = startDayOf(null, startDay)
      this.dateFrom = weekStart
      this.dateTo = weekDates(weekStart)[6]
    },

    onDateFromChange() {
      if (!this.dateFrom) return
      // Matches applyCurrentWeek()'s weekDates(...)[6]. The previous
      // Date + toISOString() version landed a day early in UTC+ timezones.
      this.dateTo = weekDates(this.dateFrom)[6]
      // fetchSheets() is scoped server-side to dateFrom/dateTo, so a manual
      // date edit needs a re-fetch or the table would keep showing whatever
      // range was last loaded.
      this.fetchSheets()
    },

    shiftWeek(direction) {
      // Step by the length of the current range so prev/next moves a whole
      // pay period (7, 14, … days), not always a single week. addDays() is
      // dayjs-based and formats in local time (a toISOString() version would
      // move 6 days instead of 7 in Australia/Perth, UTC+8).
      const hasBoth = !!(this.dateFrom && this.dateTo)
      const from = this.dateFrom || this.dateTo
      const to = this.dateTo || this.dateFrom
      const span = hasBoth ? daySpan(from, to) : 7
      this.dateFrom = addDays(from, direction * span)
      this.dateTo = addDays(to, direction * span)
      // Same as onDateFromChange() — fetchSheets() is scoped to dateFrom/
      // dateTo server-side, so prev/next week must re-fetch or the visible
      // range and the loaded data fall out of sync.
      this.fetchSheets()
    },

    resetToCurrentWeek() {
      this.applyCurrentWeek()
      this.fetchSheets()
    },

    openExportModal() {
      this.exportTradieSearch = ''
      this.$bvModal.show('supervisor-export-modal')
    },

    toggleAllTradies() {
      // Operates on the tradies currently shown (respects the search filter).
      const shown = this.filteredWorkerOptions.map((opt) => opt.value)
      if (this.allFilteredTradiesSelected) {
        this.workerFilter = this.workerFilter.filter(
          (id) => !shown.includes(id),
        )
      } else {
        this.workerFilter = [...new Set([...this.workerFilter, ...shown])]
      }
    },

    exportReport() {
      const rows = buildSupervisorExportRows(this.filtered, {
        workerFilter: this.workerFilter,
        zoneVerifiedDayKeys: this.zoneVerifiedDayKeys,
      })

      const today = new Date().toLocaleDateString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      const filename = `Supervisor Timesheet Report ${today.replace(
        /\//g,
        ' ',
      )}`

      if (this.exportFormat === 'pdf') {
        downloadRowsAsPdf(filename, rows)
        return
      }

      downloadRowsAsCsv(filename, rows)
    },

    // Decimal hours for the CSV/PDF export columns.
    dayHours(day) {
      return dayHoursDecimal(day)
    },

    dayHoursLabel(day) {
      return formatHours(dayMinutes(day)) || null
    },

    formatCurrency,

    // Total claim value of the crew plant on one sheet. A method rather than
    // a computed because it is parameterised per sheet inside the v-for, the
    // same shape as tokenRows(ts) / pagedTokenRows(ts) above.
    crewPlantTotal(ts) {
      return (ts.plant_entries || []).reduce(
        (sum, pe) => sum + Number(pe.claim_value || 0),
        0,
      )
    },

    startPlantEntryEdit(pe) {
      this.editingPlantEntryId = pe.id
      this.plantEntryEditQuantity = pe.quantity
    },

    cancelPlantEntryEdit() {
      this.editingPlantEntryId = null
      this.plantEntryEditQuantity = null
    },

    // Flat search across the currently-rendered sheets rather than trusting
    // a captured `pe` closure reference — if the user switches tabs and back
    // while a save is in flight, fetchSheets() replaces `this.sheets`
    // wholesale, and mutating a detached pre-refetch object would silently
    // never reach the screen.
    findPlantEntryById(id) {
      for (const sheet of this.sheets) {
        for (const row of sheet.rows || []) {
          for (const day of row.days || []) {
            const match = (day.plant_entries || []).find((e) => e.id === id)
            if (match) return match
          }
        }
      }
      return null
    },

    async savePlantEntryEdit(pe) {
      if (this.plantEntrySaving) return
      const quantity = Number(this.plantEntryEditQuantity)
      if (!Number.isFinite(quantity) || quantity < 0) return
      this.plantEntrySaving = true
      try {
        const updated = await this.$store.dispatch(
          'plantEquipment/amendPlantEntry',
          {
            id: pe.id,
            quantity,
          },
        )
        const live = this.findPlantEntryById(pe.id) || pe
        live.quantity = updated.quantity
        live.claim_value = updated.claim_value
        live.is_amended = updated.is_amended
        live.amendment_count = updated.amendment_count
        this.editingPlantEntryId = null
        this.plantEntryEditQuantity = null
        this.$bvToast.toast('Quantity updated.', {
          variant: 'success',
          title: 'Saved',
        })
      } catch (e) {
        const message =
          (e.response && e.response.data && e.response.data.message) ||
          'Failed to update quantity. Please try again.'
        this.$bvToast.toast(message, {
          variant: 'danger',
          title: 'Error',
          solid: true,
        })
      } finally {
        this.plantEntrySaving = false
      }
    },

    openAmendmentHistory(pe) {
      this.amendmentHistoryEntryId = pe.id
      this.amendmentHistoryPlantName = pe.plant_name
      this.amendmentHistoryOpen = true
    },

    // A finish earlier than the start means the shift ends the next morning.
    isOvernightDay(day) {
      return !!day && day.present && isOvernight(day.start, day.finish)
    },

    // Mirrors the API's _validateRowDays() so a BA sees which cell is wrong
    // instead of a bare "Failed to save changes."
    dayError(day) {
      return sharedDayError(day)
    },

    leaveTypeLabel,
    isLeaveDay,

    // "7.6h" for the leave-hours badge, or null when there's no value yet
    // (the API pre-fills the company default once the day is saved).
    leaveHoursLabel(day) {
      const h = dayLeaveHours(day)
      return h != null ? `${h}h` : null
    },

    // Mirrors the API's "Leave hours for {date} must be between 0 and 24."
    leaveHoursErrorFor(day) {
      return leaveHoursError(day)
    },

    // editRows is only populated in edit mode; canSubmit-style gate on
    // Save so the BA fixes a bad cell here instead of getting a 422 back.
    editRowsHaveErrors() {
      if (!this.editRows) return false
      return this.editRows.some((row) =>
        (row.days || []).some(
          (day) => this.dayError(day) || this.leaveHoursErrorFor(day),
        ),
      )
    },

    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id])
    },

    async deleteDraft(ts) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Delete the draft timesheet for "${
          ts.project_name || 'this project'
        }" (${this.formatWeek(ts.week_start)})? This can't be undone.`,
        {
          title: 'Delete Draft Timesheet',
          okTitle: 'Delete',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return

      this.actionLoading = ts.id
      try {
        await this.$store.dispatch('timesheet/deleteSupervisorTimesheet', ts.id)
        this.sheets = this.sheets.filter((s) => s.id !== ts.id)
        this.$bvToast.toast('Draft timesheet deleted.', {
          variant: 'success',
          title: 'Deleted',
        })
      } catch (e) {
        this.$bvToast.toast('Failed to delete draft timesheet.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.actionLoading = null
      }
    },

    async approveSheet(ts) {
      this.actionLoading = ts.id
      try {
        const updated = await this.$store.dispatch(
          'timesheet/approveSupervisorTimesheet',
          ts.id,
        )
        this._replaceSheet(updated)
        this.$bvToast.toast('Timesheet approved.', {
          variant: 'success',
          title: 'Approved',
        })
      } catch (e) {
        this.$bvToast.toast('Failed to approve timesheet.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.actionLoading = null
      }
    },

    async recallSheet(ts) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Re-open the approved timesheet for "${
          ts.project_name || 'this project'
        }" (${this.formatWeek(
          ts.week_start,
        )})? It will return to the supervisor for editing and resubmission, and the crew's approved clock entries for this week will be re-opened.`,
        {
          title: 'Recall Timesheet',
          okTitle: 'Recall',
          okVariant: 'warning',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return

      this.actionLoading = ts.id
      try {
        const updated = await this.$store.dispatch(
          'timesheet/recallSupervisorTimesheet',
          ts.id,
        )
        this._replaceSheet(updated)
        this.$bvToast.toast('Timesheet re-opened for resubmission.', {
          variant: 'success',
          title: 'Recalled',
        })
      } catch (e) {
        this.$bvToast.toast('Failed to recall timesheet.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.actionLoading = null
      }
    },

    openRejectModal(ts) {
      this.rejectForm = { sheetId: ts.id, reason: null, comment: '' }
      this.$bvModal.show('supervisor-reject-modal')
    },

    async submitReject(bvModalEvent) {
      bvModalEvent.preventDefault()
      if (!this.rejectForm.reason) return

      this.actionLoading = this.rejectForm.sheetId
      try {
        const updated = await this.$store.dispatch(
          'timesheet/rejectSupervisorTimesheet',
          {
            id: this.rejectForm.sheetId,
            rejectionReason: this.rejectForm.reason,
            rejectionComment: this.rejectForm.comment || null,
          },
        )
        this._replaceSheet(updated)
        this.$bvModal.hide('supervisor-reject-modal')
        this.$bvToast.toast('Timesheet rejected.', {
          variant: 'warning',
          title: 'Rejected',
        })
      } catch (e) {
        this.$bvToast.toast('Failed to reject timesheet.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.actionLoading = null
      }
    },

    resetRejectForm() {
      this.rejectForm = { sheetId: null, reason: null, comment: '' }
    },

    _replaceSheet(updated) {
      const idx = this.sheets.findIndex((s) => s.id === updated.id)
      if (idx !== -1) this.$set(this.sheets, idx, updated)
    },

    startEdit(ts) {
      this.editingId = ts.id
      this.editRows = JSON.parse(JSON.stringify(this.tokenRows(ts)))
      // Detached working copy, like editRows — additions/removals stage here
      // and only reach the API (and the live ts.plant_entries the rest of
      // the screen reads) on Save, so Cancel genuinely discards them.
      this.editPlantEntries = JSON.parse(JSON.stringify(ts.plant_entries || []))
      this.plantEntriesDirty = false
      this.editComment = ts.supervisor_comment || ''
      // Snapshot for the optimistic-concurrency check on save/submit — a
      // draft can now be edited by its owner (mobile) and a BA (here) at the
      // same time, which was never possible before this sheet reached a
      // reviewable status. See saveEdit/submitDraft.
      this.editUpdatedAt = ts.updated_at

      if (ts.status === 'draft') {
        const supervisorName =
          (ts.supervisor && ts.supervisor.full_name) || 'their'
        this.$bvToast.toast(`Editing ${supervisorName} draft timesheet.`, {
          variant: 'info',
          title: 'Draft takeover',
        })
      }
    },

    cancelEdit() {
      this.editingId = null
      this.editRows = null
      this.editPlantEntries = null
      this.plantEntriesDirty = false
      this.editComment = ''
      this.editUpdatedAt = null
    },

    openAddPlantModal(ts) {
      this.addPlantModalWeekDates = weekDates(ts.week_start).map((iso) => {
        const date = new Date(iso + 'T00:00:00')
        return {
          iso,
          label: formatDayHeader(iso).weekday.toUpperCase(),
          dayNum: date.getDate(),
        }
      })
      this.fetchCrewEquipmentOptions(ts.project_id)
      this.$bvModal.show('add-crew-plant-modal-admin')
    },

    async fetchCrewEquipmentOptions(projectId) {
      const params = projectId ? { project_id: projectId } : {}
      try {
        await this.$store.dispatch('plantEquipment/getPlantEquipment', params)
        const items = this.$store.getters['plantEquipment/items']
        this.crewEquipmentOptions = buildEquipmentOptions(items)
      } catch (e) {
        this.crewEquipmentOptions = []
      }
    },

    // New rows have no id yet and no server-computed rate/claim fields —
    // SupervisorCrewPlantPanel/Cards render a "pending save" placeholder for
    // those until the next successful save round-trips the real values.
    handleAddPlantSave({ date, entries }) {
      if (!this.editPlantEntries || !entries.length) return
      const equipmentById = new Map(
        this.$store.getters['plantEquipment/items'].map((i) => [i.id, i]),
      )
      entries.forEach((e) => {
        const item = equipmentById.get(e.plant_equipment_id)
        this.editPlantEntries.push({
          id: null,
          date,
          plant_equipment_id: e.plant_equipment_id,
          plant_name: item ? item.name : '',
          plant_id_rego: item ? item.plant_id_rego : null,
          quantity: e.quantity,
          rate_snapshot: null,
          rate_type_snapshot: item ? item.rate_type : null,
          claim_value: null,
          is_amended: false,
          amendment_count: 0,
        })
      })
      this.plantEntriesDirty = true
    },

    removeEditPlantEntry(pe) {
      if (!this.editPlantEntries) return
      const idx = this.editPlantEntries.indexOf(pe)
      if (idx !== -1) {
        this.editPlantEntries.splice(idx, 1)
        this.plantEntriesDirty = true
      }
    },

    toggleWorker(ri, value) {
      this.editRows = withToggledWorker(this.editRows, ri, value)
    },

    setPresent(ri, di, value) {
      this.editRows = withSetPresent(this.editRows, ri, di, value)
    },

    setDayField(ri, di, field, value) {
      this.editRows = withSetDayField(this.editRows, ri, di, field, value)
    },

    // A leave day replaces the worked flow entirely — start/finish/break are
    // cleared and stay uneditable while it's set. leave_hours is left as-is
    // when turning leave on: the API pre-fills the company default the first
    // time this day round-trips, and the BA confirms or overrides it here.
    // Turning leave back off to "Worked" clears any leave_hours value.
    setLeaveType(ri, di, value) {
      this.editRows = withSetLeaveType(this.editRows, ri, di, value)
    },

    setLeaveHours(ri, di, value) {
      this.editRows = withSetLeaveHours(this.editRows, ri, di, value)
    },

    _buildEditRowsPayload() {
      return buildEditRowsPayload(this.editRows)
    },

    async addPersonToSheet(ts) {
      this.actionLoading = ts.id
      const name =
        this.newPersonForm.name.trim() || this.newPersonForm.email.trim()

      try {
        await this.$store.dispatch('company/addTradie', {
          email: this.newPersonForm.email.trim(),
          projectId: ts.project_id,
        })
      } catch (e) {
        // company/addTradie rejects with err.response.data directly. Only
        // this call's failure means the invite itself didn't go through —
        // kept in its own try/catch so a refetch failure below (invite
        // already succeeded) can't be misreported with this message.
        const msg =
          (e && e.errors && e.errors.email && e.errors.email[0]) ||
          (e && e.message) ||
          'Failed to invite tradie.'
        this.$bvToast.toast(msg, { variant: 'danger', title: 'Error' })
        this.actionLoading = null
        return
      }

      this.newPersonForm = { name: '', email: '' }
      this.$bvToast.toast(
        `${name} has been invited and added to this project.`,
        {
          variant: 'success',
          title: 'Invited',
          autoHideDelay: 8000,
        },
      )

      try {
        // Attached to ts.project_id server-side (see CompanyController::addTradie),
        // so refetch this sheet to pick up the new row/pending_workers entry
        // immediately, the same way the supervisor's own Add Tradie flow does.
        const updated = await this.$store.dispatch(
          'timesheet/getSupervisorTimesheetSingle',
          ts.id,
        )
        this._replaceSheet(updated)
        // ts.rows feeds the read-only view, but the worker-hours table
        // renders from the detached editRows snapshot while editing (see
        // startEdit) — _replaceSheet alone leaves a newly-invited worker
        // invisible until the BA cancels and re-enters edit mode. Append
        // just the new row(s) rather than re-snapshotting, so any hours
        // already entered for other workers this session aren't discarded.
        if (this.editingId === ts.id && this.editRows) {
          const existingIds = new Set(this.editRows.map((r) => r.worker.id))
          const newRows = this.tokenRows(updated)
            .filter((r) => !existingIds.has(r.worker.id))
            .map((r) => JSON.parse(JSON.stringify(r)))
          if (newRows.length) {
            this.editRows = [...this.editRows, ...newRows]
          }
        }
      } catch (e) {
        // Non-fatal — the invite already succeeded above, this is just the
        // follow-up refresh failing, so it gets its own message rather than
        // the invite-failure toast.
        this.$bvToast.toast(
          "Invited, but couldn't refresh the sheet - reload the page to see the new row.",
          { variant: 'warning', title: 'Refresh needed' },
        )
      } finally {
        this.actionLoading = null
      }
    },

    async saveEdit(ts) {
      this.actionLoading = ts.id
      try {
        const updated = await this.$store.dispatch(
          'timesheet/updateSupervisorTimesheet',
          {
            id: ts.id,
            rows: this._buildEditRowsPayload(),
            // Omitted (not sent) unless the user actually touched crew plant
            // this session — see updateSupervisorTimesheet for why sending
            // an untouched [] would be destructive.
            plantEntries: this.plantEntriesDirty
              ? buildPlantEntriesPayload(this.editPlantEntries)
              : undefined,
            comment: this.editComment,
            updatedAt: this.editUpdatedAt,
          },
        )
        this._replaceSheet(updated)
        this.editingId = null
        this.editRows = null
        this.editPlantEntries = null
        this.plantEntriesDirty = false
        this.editComment = ''
        this.editUpdatedAt = null
        this.$bvToast.toast('Timesheet updated.', {
          variant: 'success',
          title: 'Saved',
        })
      } catch (e) {
        this._toastEditError(e, 'Failed to save changes.')
      } finally {
        this.actionLoading = null
      }
    },

    async submitDraft(ts) {
      this.actionLoading = ts.id
      try {
        const updated = await this.$store.dispatch(
          'timesheet/submitSupervisorTimesheetDraft',
          {
            id: ts.id,
            rows: this._buildEditRowsPayload(),
            plantEntries: this.plantEntriesDirty
              ? buildPlantEntriesPayload(this.editPlantEntries)
              : undefined,
            comment: this.editComment,
            updatedAt: this.editUpdatedAt,
          },
        )
        this._replaceSheet(updated)
        this.editingId = null
        this.editRows = null
        this.editPlantEntries = null
        this.plantEntriesDirty = false
        this.editComment = ''
        this.editUpdatedAt = null
        this.$bvToast.toast('Timesheet submitted.', {
          variant: 'success',
          title: 'Submitted',
        })
      } catch (e) {
        this._toastEditError(e, 'Failed to submit timesheet.')
      } finally {
        this.actionLoading = null
      }
    },

    // Shared by saveEdit/submitDraft. A 422 from _validateRowDays carries
    // per-day messages keyed rows.N.days.M.field — far more use than
    // "failed". A 409 means someone else (the drafter, on mobile, or another
    // BA) saved this sheet after it was opened here — see _assertNotStale on
    // the API side; the fix is a reload, not a retry, since editRows is a
    // stale detached copy at that point.
    _toastEditError(e, fallbackMsg) {
      const status = e && e.response && e.response.status
      if (status === 409) {
        this.$bvToast.toast(
          'This draft changed since you opened it. Reload to see the latest before saving.',
          { variant: 'warning', title: 'Out of date' },
        )
        return
      }

      const errors =
        e && e.response && e.response.data ? e.response.data.errors : null
      const first = errors ? Object.values(errors)[0] : null
      const msg = (Array.isArray(first) ? first[0] : first) || fallbackMsg
      this.$bvToast.toast(msg, { variant: 'danger', title: 'Error' })
    },

    weekDays(weekStart) {
      return weekDates(weekStart).map((iso) => {
        const { weekday, dayLabel } = formatDayHeader(iso)
        return { label: weekday, date: dayLabel }
      })
    },

    rowTotal(row) {
      const mins = row.days.reduce((sum, day) => sum + dayMinutes(day), 0)
      return mins > 0 ? formatHours(mins) : '-'
    },

    openLightbox(photos, index) {
      this.lightboxPhotos = photos.map((p) => p.url)
      this.lightboxIndex = index
      this.$bvModal.show('supervisor-photo-lightbox-modal')
    },
    resetLightbox() {
      this.lightboxPhotos = []
      this.lightboxIndex = 0
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.supervisor-timesheet-tab {
  min-height: 700px;
  background: #f8fafc;
  border-radius: 0 0 8px 8px;
}

.cursor-pointer {
  cursor: pointer;
}

.supervisor-pending-summary {
  padding: 12px 20px 0;
}

.supervisor-loading,
.supervisor-empty {
  padding: 4rem 1.5rem;
  text-align: center;
}

.supervisor-empty {
  color: #9ca3af;
}

.supervisor-empty p {
  margin: 0.75rem 0 0;
  font-size: 0.9rem;
}

.supervisor-empty-icon {
  color: #d1d5db;
  font-size: 2.25rem;
}

.supervisor-card-list {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 18px 20px 22px;
}

.timesheet-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.timesheet-card-editing {
  border-color: #93c5fd;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.08);
}

.card-body-soft {
  padding: 18px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
}

.card-body-editing {
  border-top-color: #dbeafe;
}

.detail-section {
  margin-bottom: 20px;
}

/* .audit-panel / .audit-label / .audit-entry moved to
   SupervisorAuditTrail.vue's own scoped style, .supervisor-actions /
   .supervisor-actions .btn moved to SupervisorSheetActions.vue's — same
   reason as the card-header comment above (commit 3d53908). */

/* Submission photo thumbnails — the markup/lightbox reuse TimesheetListTab's
   pattern, but its .event-photo* styles are scoped there, so redeclare them. */
.event-photos {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.photo-thumb-button {
  display: inline-flex;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.event-photo-thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
  }
}

.detail-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.detail-section-title {
  margin: 0;
  color: #111827;
  font-size: 14px;
  font-weight: 700;
}

.detail-section-description {
  margin: 3px 0 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
}

.worker-section-heading {
  padding-top: 18px;
  border-top: 1px solid #e5e7eb;
}

.crew-plant-heading-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.crew-plant-add-btn {
  border: 1px solid #006cff;
  background: #fff;
  color: #006cff;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;

  &:hover {
    background: #eaf1ff;
  }
}

.worker-count-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 4px 9px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
}

.rejection-panel {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  padding: 13px 14px;
  border: 1px solid #fecaca;
  border-radius: 10px;
  background: #fef2f2;
  color: #7f1d1d;
}

.rejection-panel-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #fee2e2;
  color: #dc2626;
}

.rejection-panel-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;

  strong {
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  p {
    margin: 4px 0 0;
    color: #991b1b;
    line-height: 1.45;
  }
}

@media (max-width: 575px) {
  .supervisor-card-list {
    padding: 14px;
  }

  .card-body-soft {
    padding: 14px;
  }

  .detail-section-heading {
    align-items: flex-start;
  }

  .detail-section-description {
    max-width: 260px;
  }
}
</style>
